import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { Select, Well } from '@pushly/aqe/lib/components'
import React, { FC } from 'react'
import { ReactNode, useEffect, useState } from 'react'
import { InfoCircleOutlined, RotateLeftOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import { InfoTooltip } from '../../info-tooltip/info-tooltip'
import { AppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import PreviewSDK from '@pushly/cuttlefish'
import classNames from 'classnames'
import { Button, Flex, Modal, Radio, Tooltip } from 'antd'
import { useThemeMode } from '@pushly/aqe'

type AppMessagePreviewProps = {
    template: AppMessageUiTemplate
    hideHeader?: boolean
    loading?: boolean | ReactNode
}

type PreviewDeviceType = 'phone' | 'tablet'

type PreviewOrientation = 'portrait' | 'landscape'
type SystemMode = 'light' | 'dark'

type PreviewPlatformOption = {
    value: DeliveryChannel
    label: ReactNode
    /** Set when the message is not delivered to this platform. */
    disabled?: boolean
    /** Why the platform cannot be previewed, shown on hover. */
    disabledReason?: string
}

type PreviewPlatformSelectProps = {
    value: DeliveryChannel
    onChange: (channel: DeliveryChannel) => void
    options: PreviewPlatformOption[]
}

type PreviewSystemModeSelectProps = {
    value: 'light' | 'dark'
    onChange: (mode: 'light' | 'dark') => void
}

type PreviewDeviceSelectProps = {
    value: PreviewDeviceType
    onChange: (deviceType: PreviewDeviceType) => void
}

type PreviewOrientationSelectProps = {
    value: PreviewOrientation
    onChange: (orientation: PreviewOrientation) => void
}

const PreviewPlatformSelect: FC<PreviewPlatformSelectProps> = ({ value, onChange, options }) => {
    return (
        <Select
            className="platform-select"
            prefix="Platform:"
            aria-label="Preview platform"
            style={{ width: 200 }}
            showSearch={false}
            value={[value]}
            onChange={(val: DeliveryChannel) => onChange(val)}
            options={options.map(({ value: option, label, disabled, disabledReason }) => ({
                value: option,
                disabled,
                label: disabledReason ? (
                    <Tooltip classNames={{ root: getClassNames('app-message-builder-tip') }} title={disabledReason}>
                        {label}
                    </Tooltip>
                ) : (
                    label
                ),
            }))}
        />
    )
}

const PreviewSystemModeSelect: FC<PreviewSystemModeSelectProps> = ({ value, onChange }) => {
    return (
        <Radio.Group
            className="theme-select"
            aria-label="Preview theme"
            buttonStyle="solid"
            optionType="button"
            size="small"
            value={value}
            onChange={(ev) => onChange(ev.target.value)}
            options={[
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
            ]}
        />
    )
}

const PreviewDeviceTypeSelect: FC<PreviewDeviceSelectProps> = ({ value, onChange }) => {
    return (
        <Select
            className="device-type-select"
            prefix="Device Type:"
            aria-label="Preview device type"
            style={{ width: 200 }}
            showSearch={false}
            value={[value]}
            onChange={(v: PreviewDeviceType) => onChange(v)}
            options={[
                { value: 'phone', label: 'Phone' },
                { value: 'tablet', label: 'Tablet' },
            ]}
        />
    )
}

const PreviewOrientationSelect: FC<PreviewOrientationSelectProps> = ({ value, onChange }) => {
    return (
        <Select
            className="orientation-select"
            prefix="Orientation:"
            aria-label="Preview orientation"
            style={{ width: 200 }}
            showSearch={false}
            value={[value]}
            onChange={(v: PreviewOrientation) => onChange(v)}
            options={[
                { value: 'portrait', label: 'Portrait' },
                { value: 'landscape', label: 'Landscape' },
            ]}
        />
    )
}

export const AppMessagePreview = ({ template, hideHeader, loading }: AppMessagePreviewProps) => {
    const previewTemplate = template

    const sortedChannels = [...previewTemplate.channels].sort()
    const defaultChannel = sortedChannels.length === 2 ? DeliveryChannel.NATIVE_IOS : sortedChannels[0]

    const [channel, setChannel] = useState(defaultChannel)

    // The platform being previewed can stop being one the message is delivered
    // to, which leaves the preview showing a platform the message will never
    // reach. Follow the message to one it will.
    useEffect(() => {
        if (!sortedChannels.length || sortedChannels.includes(channel)) {
            return
        }

        setChannel(sortedChannels[0])
        setAdvancedPreviewChannel(sortedChannels[0])
    }, [sortedChannels.join(','), channel])

    const { resolvedMode } = useThemeMode()
    const [theme, setTheme] = useState<SystemMode>(resolvedMode)
    const [landscapeVisible, setLandscapeVisible] = useState(false)

    const [advancedPreviewDeviceType, setAdvancedPreviewDeviceType] = useState<PreviewDeviceType>('phone')
    const [advancedPreviewOrientation, setAdvancedPreviewOrientation] = useState<PreviewOrientation>('portrait')
    const [advancedPreviewChannel, setAdvancedPreviewChannel] = useState<DeliveryChannel>(channel)
    const [advancedPreviewSystemMode, setAdvancedPreviewSystemMode] = useState<SystemMode>(theme)

    // Every platform the preview can show, whether or not this message reaches it.
    // A platform that is switched off stays visible and disabled, so the reader can
    // see that it is off rather than wonder where the control went.
    //
    // Short names: these pills share the preview's header with its name and its
    // orientation control, and a longer label wraps the row.
    const channelOptions = [DeliveryChannel.NATIVE_ANDROID, DeliveryChannel.NATIVE_IOS].map((option) => {
        const isDelivered = sortedChannels.includes(option)
        const label = option === DeliveryChannel.NATIVE_ANDROID ? 'Android' : 'iOS'

        return {
            value: option,
            label,
            disabled: !isDelivered,
            disabledReason: isDelivered
                ? undefined
                : `${label} is switched off for this message under Delivery Channels.`,
        }
    })

    return (
        <Well
            className={getClassNames('app-message-builder-preview', 'nested')}
            title={
                <span>
                    <span className="label-with-component-r">Preview</span>
                    <InfoTooltip
                        about="Preview"
                        title="The platform preview is a best-guess at how this app message will look across the most common devices for the selected operating system."
                    />
                </span>
            }
            hideHeader={hideHeader}
            hideFooter={true}
            // The preview's own controls belong beside its name, as every other
            // block on this page has them, leaving the body to the device.
            actions={
                <span className="preview-controls">
                    <Radio.Group
                        className="platform-select"
                        aria-label="Preview platform"
                        buttonStyle="solid"
                        optionType="button"
                        size="small"
                        value={channel}
                        onChange={(ev) => {
                            setChannel(ev.target.value)
                            setAdvancedPreviewChannel(ev.target.value)
                        }}
                    >
                        {channelOptions.map((option) => (
                            // Wrapped rather than passed as options, so the whole
                            // pill answers a hover — which is where someone whose
                            // platform is switched off will look for the reason.
                            <Tooltip
                                key={option.value}
                                classNames={{ root: getClassNames('app-message-builder-tip') }}
                                title={option.disabledReason}
                            >
                                <Radio.Button value={option.value} disabled={option.disabled}>
                                    {option.label}
                                </Radio.Button>
                            </Tooltip>
                        ))}
                    </Radio.Group>

                    <PreviewSystemModeSelect
                        value={theme}
                        onChange={(v: 'light' | 'dark') => {
                            setTheme(v)
                            setAdvancedPreviewSystemMode(v)
                        }}
                    />

                    <Tooltip
                        classNames={{ root: getClassNames('app-message-builder-tip') }}
                        title="Preview your App Message in landscape orientation and tablet mode."
                        placement="topLeft"
                    >
                        <Button
                            className="orientation-toggle"
                            size="small"
                            aria-label="Open the full preview window"
                            icon={<RotateLeftOutlined />}
                            onClick={() => {
                                setAdvancedPreviewOrientation('landscape')
                                setLandscapeVisible(true)
                            }}
                        />
                    </Tooltip>
                </span>
            }
        >
            <Flex vertical gap={8} align="center">
                <Well
                    className={classNames('app-message-portrait-preview')}
                    mode="ghost"
                    hideHeader={true}
                    hideFooter={true}
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <PreviewSDK.AppMessagePreview
                        channel={channel}
                        template={previewTemplate}
                        systemMode={theme}
                        loading={loading}
                    />
                </Well>
            </Flex>

            <Modal
                className={getClassNames('advanced-preview-modal')}
                open={landscapeVisible}
                title="App Message Previews"
                onCancel={() => {
                    setLandscapeVisible(false)
                    setAdvancedPreviewDeviceType('phone')
                    setAdvancedPreviewOrientation('portrait')
                }}
                footer={null}
                // Sized to the frame it holds rather than to a number: the frame's
                // dimensions come from the device and the orientation, and a fixed
                // width leaves a phone in landscape sitting in dead space.
                width="fit-content"
                styles={{ container: { padding: 0 }, body: { padding: 0 } }}
                centered={true}
                destroyOnHidden={true}
            >
                <div className="advanced-preview-controls">
                    <PreviewDeviceTypeSelect
                        value={advancedPreviewDeviceType}
                        onChange={(v: PreviewDeviceType) => {
                            setAdvancedPreviewDeviceType(v)
                        }}
                    />
                    <PreviewPlatformSelect
                        value={channel}
                        onChange={(val: DeliveryChannel) => {
                            setChannel(val)
                            setAdvancedPreviewChannel(val)
                        }}
                        options={channelOptions}
                    />
                    <PreviewOrientationSelect
                        value={advancedPreviewOrientation}
                        onChange={(v: PreviewOrientation) => {
                            setAdvancedPreviewOrientation(v)
                        }}
                    />

                    <span className="advanced-preview-controls-spacer" />

                    <PreviewSystemModeSelect
                        value={theme}
                        onChange={(v: 'light' | 'dark') => {
                            setTheme(v)
                            setAdvancedPreviewSystemMode(v)
                        }}
                    />
                </div>

                <div className="advanced-preview-stage">
                    <PreviewSDK.AppMessagePreview
                        template={previewTemplate}
                        channel={advancedPreviewChannel}
                        deviceType={advancedPreviewDeviceType}
                        systemMode={advancedPreviewSystemMode}
                        orientation={advancedPreviewOrientation}
                    />
                </div>
            </Modal>
        </Well>
    )
}
